import Helmet from 'react-helmet';
import MoneyBird from 'assets/img/birds/bird-money.png';
import { SAFETYWING_ADDONS as ADDONS } from 'components/Addon/consts';
import Checkbox from 'components/Checkbox';
import DatePicker from 'components/DatePicker';
import FadeInImage from 'components/FadeInImage';
import { addDays, differenceInDays, format } from 'date-fns';
import { fetchPublicOffer } from 'products/nomad_insurance/state/offers';
import queryString from 'query-string';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import history from 'routing/history';
import { AGE_GROUPS, WORLD_TRIPS_AGE_GROUPS } from './consts';

import {
  AgeRangeDropdown,
  ChildrenText,
  Content,
  DatePickerContainer,
  DateSelection,
  Illustration,
  LengthOfCoverage,
  Options,
  PricingContainer,
  SafetyNest,
  SignupButton,
  SmallText,
  StyledTooltip,
  Subtitle,
  Total,
  TotalContainer,
  TotalInDollars,
  TotalTime
} from './styled';

const getToday = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const getAgeFromGroup = (ageGroup) => {
  if (ageGroup.value === 'group1') return 1;
  if (ageGroup.value === 'group2') return 10;
  if (ageGroup.value === 'group3') return 40;
  if (ageGroup.value === 'group4') return 50;
  return 60;
};

const Pricing = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const query = useMemo(() => queryString.parse(location.search), [location.search]);

  const { publicOffer } = useSelector((state) => state.nomadInsurance.offers);
  const { isAuthenticated } = useSelector((state) => state.app.user);

  // Component state
  const [subscriptionSelected, setSubscriptionSelected] = useState(true);
  const [startDate, setStartDate] = useState(getToday);
  const [endDate, setEndDate] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [selectedAgeGroup, setSelectedAgeGroup] = useState(AGE_GROUPS[1]);
  const [totalPrice, setTotalPrice] = useState(0);

  const prevPrice = useRef(0);

  useEffect(() => {
    if (query.includeUS) {
      setSelectedAddons([ADDONS.find((addon) => addon.value === 'USA_COVERAGE')]);
    }
  }, [query]);

  useEffect(() => {
    if (publicOffer && publicOffer.planHolderTotalPrice) {
      const newPrice = publicOffer.planHolderTotalPrice;
      prevPrice.current = newPrice;
      setTotalPrice(newPrice);
    }
  }, [publicOffer?.planHolderTotalPrice]);

  const { validStartDate, validEndDate, numberOfDays } = useMemo(() => {
    const minNumberOfDays = (subscriptionSelected ? 28 : 5) - 1;

    const isStartDateValid = startDate && startDate >= getToday();
    const validStartDate = isStartDateValid ? startDate : getToday();

    const isEndDateValid =
      endDate && differenceInDays(endDate, validStartDate) >= minNumberOfDays;
    const validEndDate = isEndDateValid
      ? endDate
      : addDays(validStartDate, minNumberOfDays);

    const numberOfDays = differenceInDays(validEndDate, validStartDate) + 1;

    return {
      validStartDate,
      validEndDate,
      numberOfDays
    };
  }, [startDate, endDate]);

  useEffect(() => {
    setStartDate(validStartDate);
    setEndDate(validEndDate);
  }, [validStartDate, validEndDate]);

  useEffect(() => {
    const requestBody = {
      age: getAgeFromGroup(selectedAgeGroup),
      startDate: format(validStartDate, 'yyyy-MM-dd'),
      endDate: subscriptionSelected ? null : format(validEndDate, 'yyyy-MM-dd'),
      addons: selectedAddons.map((addon) => addon.value),
      independenceOffers: true,
      deductible: 250
    };
    dispatch(fetchPublicOffer(requestBody));
  }, [
    dispatch,
    // Fetch only when valid dates change
    validStartDate,
    validEndDate,
    selectedAddons,
    selectedAgeGroup,
    subscriptionSelected,
    numberOfDays
  ]);

  const renderTitle = () => (
    <div>
      <h1>How much does Nomad Insurance cost for US residents?</h1>

      <Subtitle>Use price calculator below to see the price.</Subtitle>
    </div>
  );

  const renderAgeRangeDropdown = () => (
    <>
      <AgeRangeDropdown
        variant="block"
        options={AGE_GROUPS}
        label="Age range"
        onChange={(val) => {
          setSelectedAgeGroup(val);
        }}
        value={selectedAgeGroup.value}
        name="agegroups"
      />
      <LengthOfCoverage>Default length of Coverage: 4 weeks (28 days)</LengthOfCoverage>
    </>
  );

  const renderSubscriptionOptions = () => (
    <>
      <Checkbox
        checked={!subscriptionSelected}
        onChange={() => setSubscriptionSelected(!subscriptionSelected)}
      >
        <b>Specific travel dates</b>
      </Checkbox>
      <StyledTooltip>
        When you choose set travel dates instead of a subscription, you pay for the whole
        trip upfront. Minimum 5 days, maximum 364 days.
      </StyledTooltip>
      {!subscriptionSelected && (
        <DateSelection>
          <DatePickerContainer>
            <b>Start date</b>
            <DatePicker
              selectsStart
              selectedDate={startDate}
              startDate={startDate}
              endDate={endDate}
              maxDate={addDays(new Date(), 180)}
              minDate={new Date()}
              onDateSelected={(date) => setStartDate(date)}
            />
          </DatePickerContainer>
          <DatePickerContainer>
            <b>End date</b>
            <DatePicker
              selectsEnd
              selectedDate={endDate}
              startDate={startDate}
              endDate={endDate}
              maxDate={addDays(startDate, 363)}
              minDate={addDays(startDate, 4)}
              onDateSelected={(date) => setEndDate(date)}
            />
          </DatePickerContainer>
        </DateSelection>
      )}
    </>
  );

  const renderTotalCount = () => {
    const getTotalTimeString = () =>
      subscriptionSelected ? '/ 4 weeks*' : `/ ${numberOfDays} days`;

    return (
      <div>
        <b>Total (USD)</b>
        <TotalInDollars
          start={prevPrice.current}
          end={totalPrice}
          duration={10}
          separator=","
          decimals={2}
        />
        <TotalTime>{getTotalTimeString()}</TotalTime>
      </div>
    );
  };

  const renderSignupButton = () => {
    const gotToSignup = () => {
      if (isAuthenticated) {
        history.push('/nomad-insurance/profile');
      } else {
        history.push('/nomad-insurance/signup');
      }
    };

    const getButtonText = () => {
      if (isAuthenticated) {
        return 'Explore your insurance';
      }
      return 'Sign me up';
    };

    return <SignupButton onClick={gotToSignup}>{getButtonText()}</SignupButton>;
  };

  const renderChildrenDisclaimer = () => {
    if (
      selectedAgeGroup === AGE_GROUPS[0] ||
      selectedAgeGroup === WORLD_TRIPS_AGE_GROUPS[0]
    ) {
      return (
        <ChildrenText>
          <p>
            <b>
              Children must be accompanied by an adult on a group policy to activate the
              $0 price.
            </b>
          </p>
          <p>
            One child older than 14 days, but less than 10 years old, per adult, is
            included in the insurance without added cost.
          </p>
          <p>
            <b>
              There is a maximum of 2 children per group / family that are included
              without added cost.
            </b>
          </p>
          <p>
            Otherwise, regular base price (same as for ages 10-39). We don’t cover
            children younger than 15 days.
          </p>
        </ChildrenText>
      );
    }
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>

      <PricingContainer bg="red">
        {renderTitle()}
        <Content>
          <Options>
            {renderAgeRangeDropdown()}
            {renderSubscriptionOptions()}
          </Options>
          <TotalContainer>
            <Illustration>
              <FadeInImage src={MoneyBird} alt="bird with a piggy bank" />
            </Illustration>
            <Total>
              {renderTotalCount()}
              {renderChildrenDisclaimer()}
              {renderSignupButton()}
              <SmallText>
                {subscriptionSelected && (
                  <p>
                    *4 weeks is equal to 28 days. We’ll charge you 3-5 days before each
                    cycle ends to process payment for the next cycle. This continues for
                    up to 364 days, after which we’ll ask you to renew.
                  </p>
                )}
                <p>*$250 deductible, $250,000 max limit.</p>
                <SafetyNest>
                  For U.S. residents the plan is administrated by WorldTrips and
                  underwritten by HCC Lloyd’s Syndicate 4141.
                </SafetyNest>
              </SmallText>
            </Total>
          </TotalContainer>
        </Content>
      </PricingContainer>
    </>
  );
};

export default Pricing;
